import { useFormik } from "formik"
import { TApiTaskDetails } from "../../api-client/types"
import { useUpdateTaskMutation } from "../queries/use-update-task-mutation"
import { useAssignTaskMutation } from "../queries/use-assign-task-mutation"
import { useMoveTaskToProjectMutation } from "../queries/use-move-task-to-project-mutation"

export type TaskFormObject = Partial<TApiTaskDetails>

interface useTaskFormikParams {
  projectId: string
  initialValues?: TaskFormObject
  onSuccess?(values: TaskFormObject): void
  onError?(values: TaskFormObject, errors: { [K in keyof TaskFormObject]?: string[] }): void
}

export const useTaskFormik = (params: useTaskFormikParams) => {
  const {
    projectId,
    initialValues,
    onSuccess,
    onError,
  } = params

  const updateTaskMutation = useUpdateTaskMutation({
    projectId,
  })

  const assignTaskMutation = useAssignTaskMutation()
  const moveTaskToProjectMutation = useMoveTaskToProjectMutation()

  const handleDefaultSubmit = (values: TaskFormObject, initialValues?: TaskFormObject) => {
    const {
      project,
      tags,
      billableRate,
      ...taskParams
    } = values

    let requestBody = { ...taskParams }

    if (project) {
      requestBody.projectId = project.id
    }

    if (tags !== undefined) {
      requestBody.tags = tags.map((t) => t.id)
    }

    if (billableRate !== undefined) {
      requestBody.billableRate = Number(parseFloat(billableRate).toFixed(2))
    }

    return updateTaskMutation.mutateAsync({ 
      taskId: values.id as string,
      body: requestBody,
    })
  }

  const handleAssignSubmit = (values: TaskFormObject, initialValues?: TaskFormObject) => {
    const {
      project,
      ...requestBody
    } = values

    if (requestBody.id === undefined) throw 'Task ID missing in task assign request!'

    const taskId = requestBody.id

    return assignTaskMutation.mutateAsync({
      taskId: taskId,
      projectId: projectId,
      targets: values.assignees
    })
  }

  const handleProjectUpdateSubmit = (values: TaskFormObject, initialValues: TaskFormObject) => {
    const {
      project,
      ...requestBody
    } = values

    if (requestBody.id === undefined) throw 'Task ID missing in task move project request!'
    if (project === undefined) throw 'Project missing in task moving to other project!'
    if (initialValues.project?.id === undefined) throw 'Project ID missing in which is moving to other project!'

    return moveTaskToProjectMutation.mutateAsync({
      projectId: initialValues.project.id,
      taskId: requestBody.id,
      newProjectId: project.id,
    })
  }

  const formik = useFormik<TaskFormObject>({
    initialValues: {
      ...initialValues,
    },
    // Ensure dirty status can be checked
    enableReinitialize: true,

    onSubmit: async (values) => {
      let submitFunction: (typeof handleAssignSubmit) = handleDefaultSubmit

      // If assignees changed, use assign submit function instead of default update
      if (JSON.stringify(values.assignees.map((a) => a.id)) !== JSON.stringify(initialValues.assignees.map((a) => a.id))) {
        submitFunction = handleAssignSubmit
      } 

      if (values.project?.id !== initialValues.project?.id) {
        submitFunction = handleProjectUpdateSubmit
      }

      try {
        const mutationResult = await submitFunction(values, initialValues)

        if (onSuccess) onSuccess(values)
      }
      catch (errors) {
        if (onError) onError(values, errors)
        formik.setErrors(errors)
      }

    }
  })

  return formik
}